/**
 * NotificationModal — componente visual standalone.
 *
 * Props:
 *   visible              {boolean}
 *   storageKey           {string}    chave única no localStorage. Se o usuário
 *                                    já clicou no botão primário, o modal não
 *                                    será exibido novamente.
 *   title                {string}
 *   body                 {string}    suporta **bold** e *italic*
 *   imageUrl             {string=}
 *   primaryButtonLabel   {string}
 *   onPrimary            {function}
 *   secondaryButtonLabel {string=}
 *   onSecondary          {function=}
 *   dismissable          {boolean=true}
 *   onDismiss            {function=}
 */

import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Modal, Tooltip } from "antd";

function escapeHtml(text) {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function renderMarkdown(input) {
  if (!input) return "";
  return escapeHtml(input)
    .replaceAll(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replaceAll(/\*([^*]+)\*/g, "<em>$1</em>")
    .replaceAll("\n", "<br />");
}

export const STORAGE_PREFIX = "tupi_modal_done:";

export default function NotificationModal({
  visible = false,
  afterClose,
  storageKey,
  title,
  body,
  imageUrl,
  primaryButtonLabel = "OK",
  onPrimary,
  secondaryButtonLabel,
  onSecondary,
  dismissable = true,
  onDismiss,
}) {
  // Se o usuário já interagiu com o botão primário, não exibir mais.
  const alreadyDone =
    storageKey && localStorage.getItem(STORAGE_PREFIX + storageKey) === "1";

  const primaryClicked = useRef(false);

  // Este modal é uma notificação flutuante — não deve bloquear o scroll da página.
  // O Ant Design v3 aplica overflow:hidden no body ao abrir qualquer Modal,
  // independente do mask. Revertemos logo após com requestAnimationFrame.
  useEffect(() => {
    if (!visible) return;
    const frame = requestAnimationFrame(() => {
      document.body.style.overflow = "";
      document.body.style.width = "";
      document.body.classList.remove("ant-scrolling-effect");
    });
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  // Não renderiza se já concluído — mas deixa visible=false ser tratado
  // pelo Modal do Ant Design para que a animação de saída seja executada.
  if (alreadyDone) return null;

  return (
    <>
      <style>{`
        .notification-inapp-wrap { pointer-events: none !important; }
        .notification-inapp-wrap .ant-modal {
          pointer-events: auto;
          position: fixed !important;
          bottom: 20px !important;
          right: 20px !important;
          top: auto !important;
          left: auto !important;
          margin: 0 !important;
          padding-bottom: 0 !important;
        }
      `}</style>
      <Modal
        visible={visible}
        afterClose={afterClose}
        footer={null}
        title={null}
        closable={false}
        mask={false}
        maskClosable={false}
        onCancel={dismissable ? onDismiss : undefined}
        width={380}
        wrapClassName="notification-inapp-wrap"
        bodyStyle={{ padding: 0, borderRadius: 8, overflow: "hidden" }}
        destroyOnClose
      >
        {/* Body */}
        <div style={{ padding: "24px 24px 12px", position: "relative" }}>
          {dismissable ? (
            <button
              onClick={onDismiss}
              style={{
                position: "absolute", top: 12, right: 12,
                background: "none", border: "none", cursor: "pointer",
                color: "#999", padding: 4, lineHeight: 1,
              }}
              aria-label="Fechar"
            >
              <Icon type="close" style={{ fontSize: 16 }} />
            </button>
          ) : (
            <Tooltip title="Esta notificação não pode ser fechada pelo usuário">
              <Icon type="lock" style={{ position: "absolute", top: 16, right: 16, color: "#ccc", fontSize: 14 }} />
            </Tooltip>
          )}

          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, lineHeight: 1.4, paddingRight: 24 }}>
            {title}
          </div>
          <div
            style={{ fontSize: 13, color: "#666", lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }}
          />

          {imageUrl && (
            <div style={{ marginTop: 12, borderRadius: 6, overflow: "hidden" }}>
              <img
                src={imageUrl}
                alt=""
                style={{ width: "100%", display: "block" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: "8px 24px 20px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {secondaryButtonLabel && (
            <Button onClick={onSecondary} style={{ fontWeight: "normal" }}>
              {secondaryButtonLabel}
            </Button>
          )}
          <Button
            type="primary"
            onClick={() => {
              if (primaryClicked.current) return;
              primaryClicked.current = true;
              onPrimary?.();
            }}
          >
            {primaryButtonLabel}
          </Button>
        </div>
      </Modal>
    </>
  );
}

NotificationModal.propTypes = {
  visible: PropTypes.bool,
  afterClose: PropTypes.func,
  storageKey: PropTypes.string,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  primaryButtonLabel: PropTypes.string,
  onPrimary: PropTypes.func,
  secondaryButtonLabel: PropTypes.string,
  onSecondary: PropTypes.func,
  dismissable: PropTypes.bool,
  onDismiss: PropTypes.func,
};
